import * as React from 'react'
import * as Reakit from 'reakit/Popover'

import { COMPONENT_ANIMATION_DURATION } from '../../constants'

export type PopoverStateReturn = ReturnType<typeof usePopoverState>

const DEFAULT_OFFSET: [number, number] = [0, 5]
const HOVER_TIMEOUT = 150

const preventDefault = (ev: React.MouseEvent<Element>) => {
  ev.preventDefault()
}

/**
 * Hook which provides state to dialogs
 */
export function usePopoverState(settings?: Reakit.PopoverInitialState) {
  const offsets = (settings?.unstable_offset ?? DEFAULT_OFFSET) as [number, number]
  const popover = Reakit.usePopoverState({
    ...settings,
    placement: settings?.placement ?? 'bottom-start',
    unstable_offset: offsets,
    animated: COMPONENT_ANIMATION_DURATION,
    modal: false,
  })

  return { ...popover, offsets }
}

export function useHoverPopover(settings?: Reakit.PopoverInitialState) {
  const popover = usePopoverState(settings)
  const showTimeout = React.useRef<number | null>(null)
  const hideTimeout = React.useRef<number | null>(null)

  const clearTimeouts = React.useRef(() => {
    if (showTimeout.current !== null) {
      window.clearTimeout(showTimeout.current)
    }
    if (hideTimeout.current !== null) {
      window.clearTimeout(hideTimeout.current)
    }
  })

  const showPopover = React.useRef(() => {
    clearTimeouts.current()
    showTimeout.current = window.setTimeout(() => {
      popover.show()
    }, HOVER_TIMEOUT)
  })

  const hidePopover = React.useRef(() => {
    clearTimeouts.current()
    hideTimeout.current = window.setTimeout(() => {
      popover.hide()
    }, HOVER_TIMEOUT)
  })

  // Unmount cleanup
  React.useEffect(() => () => clearTimeouts.current(), [])

  return {
    ...popover,
    show: showPopover.current,
    hide: hidePopover.current,
    onMouseOver: showPopover.current,
    onMouseOut: hidePopover.current,
    onMouseDown: preventDefault,
  }
}
